import { useState } from "react";
import { useTranslation } from "react-i18next";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import Dropdown from "../../components/Core/Dropdown/Dropdown";
import Modal from "../../components/Core/Modal/Modal";
import Flex from "../../components/Layout/Flex";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Contract from "../../models/Contract";
import { useContracts, useDeleteContract } from "../../networking/contract.hooks";
import { useCustomersByContract, useUpdateCustomer } from "../../networking/customer.hooks";
import { toast } from "../../utils/toast";

interface Props {
	contractId: string;
	onCancel: () => any;
	onSubmit: () => any;
}

export const ContractRemoveConfirmModal: React.FC<Props> = ({ contractId, onCancel, onSubmit }) => {
	const [newContract, setNewContract] = useState("");
	const [updatedCustomers, setUpdatedCustomers] = useState<string[]>([]);
	const { t } = useTranslation();

	const { contracts } = useContracts({ pageSize: 1000, active: true, isDeleted: false });
	const { customers, isLoading } = useCustomersByContract(contractId);
	const { mutateAsync: updateCustomer, isLoading: isUpdatingCustomer } = useUpdateCustomer();
	const { mutate: deleteContract, isLoading: isDeletingContract } = useDeleteContract();

	async function onRemoveContractAndUpdateCustomers() {
		for (const customer of customers || []) {
			try {
				// Silent: this loop toasts its own per-customer error and shouldn't
				// fire a success toast per updated customer.
				await updateCustomer({
					customerId: customer.id,
					silent: true,
					update: { contract: { id: newContract } },
				});
				setUpdatedCustomers([...updatedCustomers, customer.id]);
			} catch (err) {
				toast.error(`Kunde inte uppdatera avtal för kund ${customer.name}`);
				return;
			}
		}

		onRemoveContract();
	}

	async function onRemoveContract() {
		await deleteContract(contractId);
		onSubmit();
	}

	return (
		<Modal>
			<Flex gap="1.5rem" direction="column" minHeight="300px">
				{isLoading ? (
					<LoadingIndicator />
				) : !!customers?.length ? (
					<>
						<div>
							<AppText size="xm" color="danger" weight="medium">
								Är du säker på att du vill ta bort avtalet?
							</AppText>

							<AppText margin="0.5rem 0">
								Innan avtalet tas bort måste kunderna kopplas till ett annat avtal.
							</AppText>
						</div>
						<div>
							<AppText weight="medium">
								Avalet används av följande {customers.length} kund(er):
							</AppText>
						</div>

						<div style={{ maxHeight: 200, overflow: "auto" }}>
							{customers.map((c) => (
								<AppText key={c.id}>
									- {c.name}, #{c.customerNumber} {updatedCustomers.includes(c.id) ? "OK" : ""}
								</AppText>
							))}
						</div>

						<Flex padding="1rem" direction="column" bg="#f1f1f1">
							<AppText size="m" weight="medium">
								Välj nytt avtal för ovanstående kund(er):
							</AppText>

							<Dropdown<Contract>
								options={contracts?.filter((c) => c.id !== contractId)}
								getOptionLabel={(o) => `${o.name} ${!o.active ? "(" + t("INACTIVE") + ")" : ""}`}
								getValue={(o) => o.id}
								value={newContract}
								onChange={(c) => setNewContract(c as string)}
							/>
						</Flex>

						<Flex gap="1rem" justifyContent="flex-end" margin="4rem 0 0 0">
							<Button backgroundColor="white" textColor="grayTextDark" onClick={onCancel}>
								{t("CANCEL")}
							</Button>
							<Button
								backgroundColor="danger"
								loading={isDeletingContract || isUpdatingCustomer}
								onClick={onRemoveContractAndUpdateCustomers}
								disabled={!newContract}
							>
								Uppdatera kunder och ta bort avtal
							</Button>
						</Flex>
					</>
				) : (
					<>
						<AppText size="xm" weight="bold">
							{t("contract.delete.confirmTitle")}
						</AppText>
						<AppText size="m">{t("contract.delete.confirmText")}</AppText>
						<Flex gap="1rem" justifyContent="flex-end" margin="10rem 0 0 0">
							<Button backgroundColor="white" textColor="grayTextDark" onClick={onCancel}>
								{t("CANCEL")}
							</Button>
							<Button backgroundColor="danger" onClick={onRemoveContract}>
								{t("REMOVE")}
							</Button>
						</Flex>
					</>
				)}
			</Flex>
		</Modal>
	);
};
